import React, { useState } from "react";
import styled, { keyframes } from "styled-components";
import { useNavigate } from "react-router-dom";
import { appear } from "../animations/Appear";
import { useInView } from "react-intersection-observer";
import ProductImage from "../../assets/images/image/clothes/product-1.png";
import ProductImage2 from "../../assets/images/image/clothes/product-2.png";
import fontSize from "../../theme/fonts/FontTheme";
import colors from "../../theme/color/ColorTheme";
import { PiHeart, PiHeartFill } from "react-icons/pi";

/* ---------------- styled components ---------------- */

const heartPop = keyframes`
  0% {
    opacity: 0;
    transform: translate(-50%, -50%) scale(0.6);
  }
  20% {
    opacity: 1;
    transform: translate(-50%, -50%) scale(1);
  }
  100% {
    opacity: 0;
    transform: translate(-50%, -50%) scale(1.2);
  }
`;

const Wrapper = styled.div`
  animation: ${({ inView }) => (inView ? appear : "none")} 2s forwards;
`;

const Card = styled.div`
  cursor: pointer;
`;

const ImageWrapper = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 1 / 1.25;
  overflow: hidden;

  img {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: opacity 0.4s ease;
  }

  .first {
    opacity: ${({ $hover }) => ($hover ? 0 : 1)};
  }

  .second {
    opacity: ${({ $hover }) => ($hover ? 1 : 0)};
  }
`;

const IconWrapper = styled.div`
  display: none;
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  justify-content: space-between;
  align-items: flex-start;
  padding: 10px;
  box-sizing: border-box;

  ${Card}:hover & {
    display: flex;
    pointer-events: auto;
  }

  span {
    color: ${colors.primaryGray};
    text-transform: uppercase;
    font-size: ${fontSize.xxs};
    pointer-events: none;
  }
`;

const HeartWrapper = styled.div`
  position: relative;
  pointer-events: auto;

  svg {
    height: 20px;
    width: 20px;
    color: ${colors.line};
    stroke: currentColor;
    transition: transform 0.2s ease, fill 0.2s ease;
    &:hover {
      fill: black;
      stroke: none;
      cursor: pointer;
    }
  }

  svg[data-liked="true"] {
    fill: black;
    color: ${colors.black};
    stroke: none;
    transform: scale(1.1);
  }
`;

const CenterHeart = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  pointer-events: none;
  z-index: 5;

  svg {
    width: 80px;
    height: 80px;
    fill: white;
    animation: ${heartPop} 0.8s ease forwards;
  }
`;

const Info = styled.div`
  width: 92%;
  margin: auto;
  padding: 12px;
  box-sizing: border-box;
  background: #fff;
  display: flex;
  flex-direction: column;
  gap: 8px;
  transform: translateY(0);
  transition: transform 0.4s ease;

  ${Card}:hover & {
    transform: translateY(-50px);
  }
`;

const Row = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: clamp(0.25rem, (8 / 1920) * 100vw, 0.5rem);
`;

const Name = styled.span`
  font-size: ${fontSize.xs};
  letter-spacing: 0.12rem;
  text-transform: uppercase;
`;

const Price = styled.span`
  font-size: ${fontSize.xs};
  color: ${colors.maroon};
`;

const Material = styled.div`
  font-size: ${fontSize.xxs};
  text-transform: uppercase;
`;

const ColorSelector = styled.div`
  display: flex;
  gap: 8px;
`;

const ColorBox = styled.button`
  width: 32px;
  height: 15px;
  background: ${({ $color }) => $color};
  border: none;
  cursor: pointer;
  padding: 0;
  box-sizing: border-box;

  ${({ $active, $color }) =>
    $active &&
    `
    box-shadow: inset 0 0 0 1.5px ${$color};
    background-clip: content-box;
    padding: 2.2px;
  `}
`;

/* ---------------- component ---------------- */

function ProductCard({ product }) {
  const navigate = useNavigate();
  const [liked, setLiked] = useState(false);
  const [showHeart, setShowHeart] = useState(false);
  const { ref, inView } = useInView({ triggerOnce: true });

  const variants = [
    {
      color: "#262727",
      image1: ProductImage,
      image2: ProductImage2,
    },
    {
      color: "#134873",
      image1: ProductImage2,
      image2: ProductImage,
    },
  ];

  const [activeIndex, setActiveIndex] = useState(0);
  const [hover, setHover] = useState(false);

  const activeVariant = product.variants[activeIndex];

  return (
    <Wrapper ref={ref} inView={inView}>
      <Card onClick={() => window.location.href = (`/product/${product.slug}`)}>
        <ImageWrapper
          $hover={hover}
          onMouseEnter={() => setHover(true)}
          onMouseLeave={() => setHover(false)}
        >
          <img src={activeVariant.image[0]} alt="product" className="first" />
          <img src={activeVariant.image[1]} alt="product" className="second" />
          <IconWrapper>
            <span>COMING SOON</span>
            <HeartWrapper
              onClick={(e) => {
                e.stopPropagation();

                setLiked((prev) => {
                  if (!prev) {
                    setShowHeart(true);
                    setTimeout(() => setShowHeart(false), 800);
                  }
                  return !prev;
                });
              }}
            >
              {liked ? <PiHeartFill data-liked="true" /> : <PiHeart />}
            </HeartWrapper>
          </IconWrapper>
          {showHeart && (
            <CenterHeart>
              <PiHeartFill />
            </CenterHeart>
          )}
        </ImageWrapper>

        <Info>
          <Row style={{ display: "flex", justifyContent: "space-between" }}>
            <Name>{product.name}</Name>
            {/* <Price>${product.price}</Price> */}
          </Row>

          <Material>{product.material}</Material>

          <ColorSelector>
            {product.variants.map((variant, index) => (
              <ColorBox
                key={index}
                $color={variant.color}
                $active={index === activeIndex}
                onClick={(e) => {
                  e.stopPropagation();
                  setActiveIndex(index);
                }}
              />
            ))}
          </ColorSelector>
        </Info>
      </Card>
    </Wrapper>
  );
}

export default ProductCard;
